import type { ReactNode } from 'react'

import { cn, Popover, PopoverContent, PopoverTrigger } from '@massilia/ui'
import { HelpCircle } from 'lucide-react'

import { useInstallmentPartner } from './use-installment-partner'

/**
 * Explication des deux modes de paiement portés par les cartes de vol.
 *
 * Deux rendus, choisis en CSS pur — pas de `useIsMobile`, pas d'état :
 *
 * - **Mobile** : une légende de deux lignes, une par financeur, qui garde les
 *   deux aplats. Les pavés empilés coûtaient plus de 250 px avant le premier
 *   vol, la page des résultats s'ouvrait sur une explication au lieu des
 *   offres. Les deux lignes ne diffèrent que par « sans frais » / « avec
 *   frais », et c'est exactement la distinction à faire pendant qu'on compare
 *   des vols. Le détail (acompte, mensualités) part dans un popover ouvert au
 *   doigt : un `(?)` invite au tap mieux qu'un chevron, et contrairement à un
 *   dépliant il ne repousse pas la liste des offres — ce qui était le
 *   problème d'origine.
 * - **Desktop** : les deux pavés explicatifs, où la place ne manque pas.
 *
 * Le `(?)` est le vocabulaire déjà en place sur cette page : `StopsIndicator`
 * ouvre le détail des escales avec le même `HelpCircle` dans un `Popover`.
 *
 * Aplats de couleur assumés (bleu Massilia / rose partenaire), demandés au
 * ticket. Trois points non négociables si on y touche :
 *
 * 1. La page résultats est sur `bg-blue-50`. Un bloc en `blue-50` s'y fond au
 *    point de disparaître — d'où le `-100` bordé de `-200`, pas plus clair.
 * 2. Les logos Oney (JPEG) et FLOA (PNG sans alpha) portent leur propre fond
 *    blanc : sur un aplat teinté ils dessineraient un rectangle. La pastille
 *    blanche est là pour ça, et elle est mise des deux côtés pour que les deux
 *    logos reposent sur le même socle. Ce socle est à largeur fixe (`w-11`) :
 *    le logo FLOA est carré quand Massilia et Oney sont en 2,5:1, donc à
 *    hauteur égale ils ne font pas la même largeur et les deux lignes de texte
 *    ne commençaient pas au même endroit.
 * 3. Le libellé mobile est court (« En plusieurs fois, sans frais ») parce que
 *    le socle et le `(?)` mangent la largeur : la phrase complète repassait à
 *    la ligne sur un écran de 375 px, et deux lignes par financeur rendaient
 *    la légende aussi encombrante que les pavés qu'elle remplace.
 *
 * Le texte se teinte depuis la couleur du bloc, jamais en gris : du gris sur un
 * aplat coloré se lit comme une erreur de rendu.
 */

/** Une seule source pour chaque explication : mobile la met dans le popover, desktop dans le pavé. */
const MASSILIA_DETAIL = (
  <>
    Versez un acompte à la réservation, puis payez en plusieurs fois{' '}
    <span className="font-semibold text-green-800">sans frais</span>, tous les mois jusqu&apos;au
    départ.
  </>
)

const partnerDetail = (name: string) => (
  <>
    Payez en 1 fois, ou en plusieurs fois avec frais via notre partenaire{' '}
    <span className="font-semibold text-pink-950">{name}</span>.
  </>
)

const TONES = {
  massilia: 'border-blue-200 bg-blue-100 text-blue-950',
  partner: 'border-pink-200 bg-pink-100 text-pink-950',
} as const

interface PaymentLineProps {
  detail: ReactNode
  label: string
  logoAlt: string
  logoSrc: string
  tone: keyof typeof TONES
}

export function PaymentInfoBanner() {
  const partner = useInstallmentPartner()

  return (
    <>
      <div className="mb-4 space-y-2 md:hidden">
        <PaymentLine
          detail={MASSILIA_DETAIL}
          label="En plusieurs fois, sans frais"
          logoAlt="Massilia Voyages"
          logoSrc="/logos/massilia-logo.png"
          tone="massilia"
        />
        <PaymentLine
          detail={partnerDetail(partner.name)}
          label="En plusieurs fois, avec frais"
          logoAlt={partner.name}
          logoSrc={partner.logoSrc}
          tone="partner"
        />
      </div>

      <div className="mb-4 hidden gap-3 md:grid md:grid-cols-2">
        <div className="rounded-lg border border-blue-200 bg-blue-100 p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex shrink-0 items-center rounded bg-white px-1.5 py-1">
              <img alt="Massilia Voyages" className="h-5 w-auto" src="/logos/massilia-logo.png" />
            </span>
            <span className="text-sm font-semibold text-blue-950">
              Paiement en plusieurs fois Massilia Voyages
            </span>
          </div>
          <p className="text-sm leading-relaxed text-blue-950/80">{MASSILIA_DETAIL}</p>
        </div>

        <div className="rounded-lg border border-pink-200 bg-pink-100 p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex shrink-0 items-center rounded bg-white px-1.5 py-1">
              <img alt={partner.name} className="h-5 w-auto" src={partner.logoSrc} />
            </span>
            <span className="text-sm font-semibold text-pink-950">
              Paiement en plusieurs fois {partner.name}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-pink-950/80">{partnerDetail(partner.name)}</p>
        </div>
      </div>
    </>
  )
}

/** Ligne de légende mobile : toute la ligne est la cible tactile, le `(?)` en est le signal. */
function PaymentLine({ detail, label, logoAlt, logoSrc, tone }: PaymentLineProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex w-full cursor-pointer items-center gap-2.5 rounded-lg border p-3 text-left text-sm shadow-sm transition-colors',
            TONES[tone]
          )}
          type="button"
        >
          <span className="flex shrink-0 items-center rounded bg-white px-1.5 py-1">
            <img alt={logoAlt} className="h-4 w-11 object-contain" src={logoSrc} />
          </span>
          <span>{label}</span>
          <HelpCircle aria-hidden className="ml-auto size-4 shrink-0 opacity-60" />
          <span className="sr-only">En savoir plus</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-[min(20rem,calc(100vw-2rem))] p-3 text-sm leading-relaxed shadow-lg',
          TONES[tone]
        )}
        side="bottom"
        sideOffset={6}
      >
        {detail}
      </PopoverContent>
    </Popover>
  )
}
